import React, { useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  DatePicker,
  Field,
  Flex,
  Message,
  Modal,
  Input,
  Text,
} from '@workwhile/ui'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  endOfQuarter,
  format,
  getQuarter,
  startOfQuarter,
  subQuarters,
} from 'date-fns'
import {
  isEndedQuarterStart,
  payHealthStipendSchema,
  PayHealthStipendSchema,
} from '../schema'
import { usePayHealthStipendFlowContextValue } from '../context/PayHealthStipendFlowProvider'
import { HealthStipendResult } from './HealthStipendResult'

const VELOCITY_LIMIT_WARNING =
  'This skips the $1,500 transfer limit and the other velocity controls. Use it only when you are sure the amounts are correct.'

const defaultValues: Partial<PayHealthStipendSchema> = {
  quarterStartDate: undefined,
  workerId: '',
  overrideVelocityControls: false,
}

const formatQuarterLabel = (quarterStart: Date) =>
  `Q${getQuarter(quarterStart)} ${format(quarterStart, 'yyyy')}`

const formatQuarterRange = (quarterStart: Date) =>
  `${format(quarterStart, 'MMM d, yyyy')} – ${format(endOfQuarter(quarterStart), 'MMM d, yyyy')}`

const getRecentEndedQuarterPresets = (count: number) => {
  const lastEndedQuarterStart = startOfQuarter(subQuarters(new Date(), 1))
  return Array.from({ length: count }, (_, index) => {
    const quarterStart = subQuarters(lastEndedQuarterStart, index)
    return { label: formatQuarterLabel(quarterStart), value: quarterStart }
  })
}

type Step = 'form' | 'confirm'

export const PayHealthStipendFlowMain = () => {
  const {
    state: {
      isOpen,
      mutationState: { isPending, isSuccess, errorMessage, data },
    },
    actions: { open, close, submit, reset: resetMutation },
  } = usePayHealthStipendFlowContextValue()

  const [step, setStep] = useState<Step>('form')
  const [presets] = useState(() => getRecentEndedQuarterPresets(4))

  const {
    control,
    handleSubmit,
    getValues,
    reset,
    formState: { errors },
  } = useForm<PayHealthStipendSchema>({
    resolver: zodResolver(payHealthStipendSchema),
    defaultValues,
  })

  const handleClose = () => {
    reset(defaultValues)
    setStep('form')
    close()
  }

  const handleReview = handleSubmit(() => setStep('confirm'))

  const handleBack = () => {
    resetMutation()
    setStep('form')
  }

  const handleConfirm = () => {
    const values = getValues()
    submit({
      quarterStartDate: format(values.quarterStartDate, 'yyyy-MM-dd'),
      workerId: Number(values.workerId),
      overrideVelocityControls: values.overrideVelocityControls,
    })
  }

  const renderCta = () => {
    if (isSuccess) {
      return (
        <Button variant="primary" onClick={handleClose}>
          Done
        </Button>
      )
    }
    if (step === 'confirm') {
      return (
        <Flex>
          <Button variant="text" onClick={handleBack} disabled={isPending}>
            Back
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={isPending}
            loading={isPending}
          >
            Confirm and pay
          </Button>
        </Flex>
      )
    }
    return (
      <Flex>
        <Button variant="text" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleReview}>
          Review
        </Button>
      </Flex>
    )
  }

  const renderBody = () => {
    if (isSuccess && data) {
      return (
        <Box mt={3}>
          <HealthStipendResult
            workerId={Number(getValues().workerId)}
            result={data}
          />
        </Box>
      )
    }

    if (step === 'confirm') {
      const values = getValues()
      return (
        <Box mt={3}>
          <Message variant="warning" mb={3} borderRadius="small">
            This action moves money. Check the details before you confirm. If
            the worker is already paid for this quarter, the backend skips the
            payment.
          </Message>
          <Text>
            <Text as="span" fontWeight="bold">
              Quarter:
            </Text>{' '}
            {formatQuarterLabel(values.quarterStartDate)} (
            {formatQuarterRange(values.quarterStartDate)})
          </Text>
          <Text mt={2}>
            <Text as="span" fontWeight="bold">
              Worker ID:
            </Text>{' '}
            {values.workerId.trim()}
          </Text>
          <Text mt={2}>
            <Text as="span" fontWeight="bold">
              Override velocity controls:
            </Text>{' '}
            {values.overrideVelocityControls ? 'Yes' : 'No'}
          </Text>
          {values.overrideVelocityControls ? (
            <Message variant="error" mt={3} borderRadius="small">
              {VELOCITY_LIMIT_WARNING}
            </Message>
          ) : null}
          {errorMessage ? (
            <Message variant="error" mt={3} borderRadius="small">
              {errorMessage}
            </Message>
          ) : null}
        </Box>
      )
    }

    return (
      <Box mt={3}>
        <Text mb={3}>
          Pay the Prop 22 quarterly health stipend to one worker.
        </Text>
        <Field
          label="Quarter start date"
          error={errors.quarterStartDate?.message?.toString()}
        >
          <Controller
            name="quarterStartDate"
            control={control}
            render={({ field }) => (
              <DatePicker
                mode="single"
                aria-label="Quarter start date"
                placeholder="Select the first day of a quarter"
                selected={field.value}
                defaultMonth={field.value ?? presets[0].value}
                disabled={(date: Date) => !isEndedQuarterStart(date)}
                presets={presets}
                onSelect={(date) => field.onChange(date as Date | undefined)}
              />
            )}
          />
        </Field>
        <Box mt={3}>
          <Field label="Worker ID" error={errors.workerId?.message?.toString()}>
            <Controller
              name="workerId"
              control={control}
              render={({ field }) => (
                <Input
                  aria-label="Worker ID"
                  placeholder="Enter one worker ID"
                  inputMode="numeric"
                  value={field.value}
                  onChange={(event) => field.onChange(event.target.value)}
                  onBlur={field.onBlur}
                />
              )}
            />
          </Field>
        </Box>
        <Box mt={3}>
          <Controller
            name="overrideVelocityControls"
            control={control}
            render={({ field }) => (
              <Checkbox
                value={field.value}
                onChange={(checked) => field.onChange(checked)}
              >
                <Text>Override velocity controls</Text>
              </Checkbox>
            )}
          />
          <Message variant="warning" mt={2} borderRadius="small">
            {VELOCITY_LIMIT_WARNING}
          </Message>
        </Box>
      </Box>
    )
  }

  return (
    <>
      <Button variant="secondary" kind="medium" onClick={open}>
        Pay health stipend
      </Button>
      <Modal
        open={isOpen}
        onClose={handleClose}
        position="top"
        title={
          isSuccess
            ? 'Health stipend result'
            : step === 'confirm'
              ? 'Confirm health stipend payout'
              : 'Pay health stipend'
        }
        customCta={renderCta()}
      >
        {renderBody()}
      </Modal>
    </>
  )
}
